"use client";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/Textarea";
import { Label } from "../components/ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { Badge } from "../components/ui/Badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/Collapsible";
import { Switch } from "../components/ui/Switch";
import { useState, useEffect } from "react";
import { categoryService, Category } from "../services/categories";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Package, ChevronDown, ChevronRight, X, ImageIcon, ExternalLink, Loader2 } from "lucide-react";
import { getImageUrl, decodeHtmlEntities } from "../utils";
import { apiFormData } from "../services/api";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/AlertDialog";

interface CategoriasPageProps {
  onNavigate?: (page: string) => void;
}

export function CategoriasPage({ onNavigate: _onNavigate }: CategoriasPageProps = {}) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddPopup, setShowAddPopup] = useState<{ visible: boolean; type: string; editingId?: number }>({ visible: false, type: "" });
  const [popupStep, setPopupStep] = useState<"select" | "form">("select");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [popupData, setPopupData] = useState({
    name: "",
    description: "",
    image: "",
    parent_id: null as number | null,
    active: true,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ visible: boolean; id?: number }>({ visible: false });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getAll({ all: 1 });
      setCategories(response.data);
    } catch (error) {
      toast.error("Erro ao carregar categorias", {
        description: "Não foi possível buscar as categorias do servidor.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const topLevelCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId: number) => categories.filter(c => c.parent_id === parentId);

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const openAddPopup = () => {
    setPopupStep("select");
    setShowAddPopup({ visible: true, type: "" });
  };

  const selectType = (type: "categoria" | "subcategoria") => {
    setPopupData({ name: "", description: "", image: "", parent_id: null, active: true });
    setShowAddPopup({ visible: true, type });
    setPopupStep("form");
  };

  const closeAddPopup = () => {
    setShowAddPopup({ visible: false, type: "" });
    setPopupStep("select");
  };

  const savePopupData = async () => {
    try {
      if (showAddPopup.editingId) {
        await categoryService.update(showAddPopup.editingId, popupData);
        toast.success("Categoria atualizada com sucesso");
      } else {
        await categoryService.create(popupData);
        toast.success("Categoria criada com sucesso");
      }
      fetchCategories();
      closeAddPopup();
    } catch (error: any) {
      toast.error("Erro ao salvar", {
        description: error.response?.data?.message || "Ocorreu um erro ao salvar a categoria.",
      });
    }
  };

  const handleDelete = async (id: number) => {
    setDeleteConfirm({ visible: true, id });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.id) return;
    try {
      await categoryService.delete(deleteConfirm.id);
      toast.success("Categoria excluída com sucesso");
      fetchCategories();
    } catch (error: any) {
      toast.error("Erro ao excluir", {
        description: error.response?.data?.message || "Ocorreu um erro ao excluir.",
      });
    } finally {
      setDeleteConfirm({ visible: false });
    }
  };

  const handleEdit = (category: Category) => {
    setPopupData({
      name: category.name,
      description: category.description || "",
      image: category.image || "",
      parent_id: category.parent_id,
      active: category.active,
    });
    setShowAddPopup({ 
      visible: true, 
      type: category.parent_id ? "subcategoria" : "categoria",
      editingId: category.id 
    });
    setPopupStep("form");
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'categories');

      const response = await apiFormData.post('/api/admin/upload/send', formData);
      setPopupData({ ...popupData, image: response.data.path ?? "" });
      toast.success("Imagem enviada com sucesso");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingImage(false);
    }
  };

  const getStoreUrl = (slug: string) => {
    const storeUrl = import.meta.env.VITE_STORE_URL || "";
    return `${storeUrl}/${slug}`;
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Categorias</h1>
        <p className="text-[#68686A]">Organize seus produtos por categorias</p>
      </div>
      <div className="flex justify-end mb-6">
        <Button
          onClick={openAddPopup}
          className="bg-[#D4C070] hover:bg-[#849D70] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {/* Lista de Categorias */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4C070]"></div>
        </div>
      ) : (
        <Card className="mb-6 border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-0">
            <div className="divide-y">
              {topLevelCategories.map((category) => (
                <Collapsible
                  key={category.id}
                  open={expandedCategories.includes(category.id)}
                  onOpenChange={() => toggleCategory(category.id)}
                >
                <CollapsibleTrigger asChild>
                  <div className="flex flex-col sm:flex-row sm:items-center p-3 sm:p-4 hover:bg-gray-50 cursor-pointer gap-2 sm:gap-0">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className="mr-2 sm:mr-3 flex-shrink-0">
                        {getSubcategories(category.id).length > 0 ? (
                          expandedCategories.includes(category.id) ? (
                            <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#68686A]" />
                          ) : (
                            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-[#68686A]" />
                          )
                        ) : (
                          <div className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 mr-2 sm:mr-4">
                        <div className="flex items-center gap-2 sm:gap-3 mb-0 sm:mb-1">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0 border border-gray-200">
                            {category.image ? (
                              <ImageWithFallback src={getImageUrl(category.image)} alt={category.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                              <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate max-w-[140px] sm:max-w-xs">{decodeHtmlEntities(category.name)}</h3>
                              <a 
                                href={getStoreUrl(category.slug)} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#D4C070] hover:text-[#849D70] transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                              </a>
                              <Badge
                                variant={category.active ? "default" : "secondary"}
                                className={`text-[9px] sm:text-xs px-1 sm:px-2 py-0 h-4 sm:h-5 ${category.active
                                  ? 'bg-[#849D70] text-white border-[#849D70]'
                                  : 'bg-[#C2452D] text-white border-[#C2452D]'}`}
                              >
                                {category.active ? "Ativa" : "Inativa"}
                              </Badge>
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-500 truncate w-full">{category.slug}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between sm:justify-end pl-8 sm:pl-0 mt-1 sm:mt-0 w-full sm:w-auto border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-100">
                      <div className="flex items-center gap-1.5 sm:gap-2 mr-2 sm:mr-4">
                        <Package className="h-3 w-3 sm:h-4 sm:w-4 text-[#68686A]" />
                        <span className="text-xs sm:text-sm text-[#68686A]">{category.products_count ?? 0} produtos</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(category);
                          }}
                        >
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(category.id);
                          }}
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="bg-[#EEF1F2] border-t border-gray-200">
                    {getSubcategories(category.id).map((subcategory) => (
                      <div key={subcategory.id} className="flex flex-col sm:flex-row sm:items-center p-3 sm:p-4 pl-6 sm:pl-12 hover:bg-gray-100 gap-2 sm:gap-0">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="flex-1 min-w-0 mr-2 sm:mr-4">
                            <div className="flex items-center gap-2 sm:gap-3 mb-0 sm:mb-1">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded overflow-hidden flex-shrink-0 border border-gray-200">
                                {subcategory.image ? (
                                  <ImageWithFallback src={getImageUrl(subcategory.image)} alt={subcategory.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <ImageIcon className="h-3 w-3 sm:h-4 w-4 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                  <h4 className="font-medium text-gray-800 text-sm sm:text-base truncate max-w-[140px] sm:max-w-xs">{decodeHtmlEntities(subcategory.name)}</h4>
                                  <a 
                                    href={getStoreUrl(subcategory.slug)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-[#D4C070] hover:text-[#849D70] transition-colors"
                                  >
                                    <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </a>
                                  <Badge
                                    variant={subcategory.active ? "default" : "secondary"}
                                    className={`text-[9px] sm:text-xs px-1 sm:px-2 py-0 h-4 sm:h-5 ${subcategory.active
                                      ? 'bg-[#849D70] text-white border-[#849D70]'
                                      : 'bg-[#C2452D] text-white border-[#C2452D]'}`}
                                  >
                                    {subcategory.active ? "Ativa" : "Inativa"}
                                  </Badge>
                                </div>
                                <p className="text-[10px] sm:text-[10px] text-gray-500 truncate w-full">{subcategory.slug}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end pl-9 sm:pl-0 mt-1 sm:mt-0 w-full sm:w-auto border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-200 sm:border-transparent">
                          <div className="flex items-center gap-1.5 sm:gap-2 mr-2 sm:mr-4">
                            <Package className="h-3 w-3 sm:h-4 w-4 text-[#68686A]" />
                            <span className="text-xs sm:text-sm text-[#68686A]">{subcategory.products_count ?? 0} produtos</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                              onClick={() => handleEdit(subcategory)}
                            >
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                              onClick={() => handleDelete(subcategory.id)}
                            >
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </CardContent>
      </Card>
    )}



      {/* Popup de adição */}
      {showAddPopup.visible && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/20 backdrop-blur-md">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl">
            <button
              onClick={closeAddPopup}
              className="absolute top-3 right-3 text-white bg-[#D4C070] hover:bg-[#D4C070] rounded-full p-1 cursor-pointer z-[60]"
            >
              <X className="h-5 w-5" />
            </button>

            {popupStep === "select" ? (
              <>
                <h3 className="text-lg font-semibold mb-6 text-gray-900 text-center">O que você deseja adicionar?</h3>
                <div className="space-y-3">
                  <Button
                    onClick={() => selectType("categoria")}
                    className="w-full justify-start bg-[#EEF1F2] hover:bg-[#D4C070] hover:text-white text-[#68686A]"
                  >
                    Categoria
                  </Button>
                  <Button
                    onClick={() => selectType("subcategoria")}
                    className="w-full justify-start bg-[#EEF1F2] hover:bg-[#D4C070] hover:text-white text-[#68686A]"
                  >
                    Subcategoria
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  {showAddPopup.editingId ? "Editar " : "Adicionar "}
                  {showAddPopup.type === "subcategoria" ? "Subcategoria" : "Categoria"}
                </h3>
                <div className="space-y-4">
                  {showAddPopup.type === "subcategoria" && (
                    <>
                      <div>
                        <Label htmlFor="parent-category" className="block text-sm font-medium text-gray-700">Categoria Pai</Label>
                          <Select
                            value={popupData.parent_id?.toString() || ""}
                            onValueChange={(value) => setPopupData({ ...popupData, parent_id: parseInt(value) })}
                          >
                            <SelectTrigger className="w-full border-gray-300">
                              <SelectValue placeholder="Selecione uma categoria" />
                            </SelectTrigger>
                            <SelectContent>
                              {topLevelCategories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id.toString()}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                      </div>
                    </>
                  )}
                  <div>
                    <Label className="block text-sm font-medium text-gray-700 mb-2">Imagem da Categoria</Label>
                    <div className="flex items-start gap-4">
                      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center relative group">
                        {popupData.image ? (
                          <>
                            <ImageWithFallback src={getImageUrl(popupData.image)} alt="Preview" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button 
                                onClick={() => setPopupData({ ...popupData, image: "" })}
                                className="text-white hover:text-red-400"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-2">
                            {uploadingImage ? (
                              <Loader2 className="h-6 w-6 animate-spin text-[#D4C070] mx-auto" />
                            ) : (
                              <>
                                <ImageIcon className="h-6 w-6 text-gray-400 mx-auto" />
                                <span className="text-[10px] text-gray-500 mt-1 block">Clique abaixo</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="text-xs border-gray-300 file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#D4C070]/10 file:text-[#D4C070] hover:file:bg-[#D4C070]/20"
                        />
                        <p className="text-[10px] text-gray-500">Recomendado: 400x400px. Máx: 2MB.</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="item-name" className="block text-sm font-medium text-gray-700">
                      {showAddPopup.type === "subcategoria" ? "Nome da Subcategoria" : "Nome da Categoria"}
                    </Label>
                    <Input
                      id="item-name"
                      value={popupData.name}
                      onChange={(e) => setPopupData({ ...popupData, name: e.target.value })}
                      placeholder={showAddPopup.type === "subcategoria" ? "Ex: Salto Alto" : "Ex: Sapatos"}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="item-description" className="block text-sm font-medium text-gray-700">
                      Descrição
                    </Label>
                    <Textarea
                      id="item-description"
                      value={popupData.description}
                      onChange={(e) => setPopupData({ ...popupData, description: e.target.value })}
                      placeholder="Descreva brevemente..."
                      rows={3}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="item-active" className="text-sm font-medium text-gray-700">Status</Label>
                      <p className="text-xs text-gray-500">Define se a categoria está visível na loja</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${popupData.active ? 'text-[#849D70]' : 'text-[#C2452D]'}`}>
                        {popupData.active ? 'Ativa' : 'Inativa'}
                      </span>
                      <Switch
                        id="item-active"
                        checked={popupData.active}
                        onCheckedChange={(checked) => setPopupData({ ...popupData, active: checked })}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  {!showAddPopup.editingId && (
                    <Button
                      variant="outline"
                      onClick={() => setPopupStep("select")}
                      className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                    >
                      Voltar
                    </Button>
                  )}
                  <Button
                    onClick={savePopupData}
                    className="bg-[#D4C070] hover:bg-[#849D70] text-white"
                  >
                    Salvar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={deleteConfirm.visible} onOpenChange={(open) => !open && setDeleteConfirm({ visible: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente a categoria e poderá afetar produtos vinculados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#C2452D] hover:bg-[#A13925] text-white border-none cursor-pointer">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

export default CategoriasPage;
